import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from '@/shared/components/Snackbar/SnackbarProvider'
import { useScheduleStore } from '../store/useScheduleStore'
import { SCHEDULE_USE_API } from '../config'
import ScheduleAPI from '../api/ScheduleAPI'
import { SCHEDULE_QUERY } from '../api/ScheduleAPI.model'
import {
  serializeColors,
  serializeWeek,
  shiftsInWeek,
  storeToWeekDto,
  weekDtoToStore
} from '../api/scheduleMapper'
import { weeksReadThisSession } from '../api/sessionWeeks'
import { crossesMidnight, dayjs } from '../utils/schedule'

/**
 * Quiet period before a change is written.
 *
 * Not a delay for its own sake. The endpoint takes a whole week per request,
 * and several edits arrive in a burst: the colour picker fires on every
 * pointer move of the wheel, and a drag that lands on a new day is a move plus
 * whatever the user does next. Without a pause each of those would be its own
 * full-week PUT. Short enough that the write is finished long before anyone
 * would have reached for the Save button this replaced.
 */
const QUIET_PERIOD_MS = 600

interface ScheduleSave {
  /** Weeks whose last write failed, still local only, and offered for retry. */
  unsavedWeeks: string[]
  retry: () => void
  /** False while the backend endpoints are switched off; nothing is written. */
  isEnabled: boolean
}

/**
 * Persists roster edits on their own, week by week, as soon as they settle.
 *
 * There is no explicit save: a roster is built by adding shifts one at a time
 * over several minutes, and a single button at the end is a button somebody
 * eventually forgets, losing every shift added since the last press. Each
 * confirmed change is written instead, and the toolbar reports what state the
 * week is in rather than asking for an action.
 *
 * Two properties fall out of the endpoint being whole-week:
 *
 *  - what gets written is a WEEK, not the edit, so one confirmed shift costs
 *    one PUT of the seven days around it, and edits that land together are
 *    coalesced into a single request by the quiet period above;
 *  - a week is written only once it has been read. The dirty check keys off
 *    `savedSnapshotByWeek`, which only holds weeks that came back from a GET
 *    (or from a write). A week whose fetch failed has no baseline, so it is
 *    never pushed, a PUT replaces the week, and writing one we could not read
 *    would delete whatever the server holds there.
 */
export function useScheduleSave(): ScheduleSave {
  const { t } = useTranslation()
  const { showSnackbar } = useSnackbar()
  const queryClient = useQueryClient()

  const shifts = useScheduleStore(state => state.shifts)
  // The committed map, not the live one: the picker emits a colour every 150ms
  // while the pointer is down, and a request per tick is what this hook is
  // careful not to do. See `committedColorsByUserId` in the store.
  const committedColors = useScheduleStore(
    state => state.committedColorsByUserId
  )
  const savedSnapshotByWeek = useScheduleStore(
    state => state.savedSnapshotByWeek
  )
  const savedColorsSnapshot = useScheduleStore(
    state => state.savedColorsSnapshot
  )

  const [unsavedWeeks, setUnsavedWeeks] = useState<string[]>([])

  /**
   * Content of each week as the last write attempt saw it. A failed write
   * leaves its week dirty for ever, so without this the effect below would
   * retry the same rejected payload on every render.
   */
  const attempted = useRef(new Map<string, string>())
  const inFlight = useRef(new Set<string>())

  const dirtyWeeks = useMemo(
    () =>
      Object.keys(savedSnapshotByWeek).filter(
        weekStart =>
          serializeWeek(shifts, weekStart) !== savedSnapshotByWeek[weekStart]
      ),
    [shifts, savedSnapshotByWeek]
  )

  /**
   * Colours belong to the restaurant, not to a week, and every week's PUT
   * carries the whole map, so one request persists them, whichever week it
   * rides with. Comparing them per week instead is what turned a single colour
   * change into a full write of every loaded week.
   *
   * The serialized value, not a boolean: it is what the effect below keys off,
   * and a boolean would stay `true` across a whole colour drag, so the quiet
   * period would never restart and the write would fire mid-drag.
   */
  const colorsSerialized = serializeColors(committedColors)
  const colorsDirty =
    savedColorsSnapshot !== null && colorsSerialized !== savedColorsSnapshot

  const writeWeek = useCallback(
    async (weekStart: string): Promise<void> => {
      if (!SCHEDULE_USE_API || inFlight.current.has(weekStart)) return

      const state = useScheduleStore.getState()
      const sent = serializeWeek(state.shifts, weekStart)
      const sentColors = serializeColors(state.committedColorsByUserId)

      // Recorded before the attempt, not after it: a payload the server
      // rejects must not be sent again until its content changes.
      attempted.current.set(weekStart, sent)

      // A shift running past midnight is stored past 1440 (16:00→02:00 is
      // 960→1560), but the endpoint caps endMinutes at 1440, so one bar shift
      // would 400 the whole week. Refusing before the request is the only
      // lossless answer: clamping to midnight silently shortens someone's paid
      // hours, and splitting the shift puts its tail in the following week,
      // which the endpoint rejects as out of range.
      const overnight = shiftsInWeek(state.shifts, weekStart).filter(
        crossesMidnight
      )

      if (overnight.length) {
        setUnsavedWeeks(weeks =>
          weeks.includes(weekStart) ? weeks : [...weeks, weekStart]
        )
        showSnackbar(
          t('ALERTS.SAVE_SCHEDULE_OVERNIGHT_ERROR.TITLE'),
          t('ALERTS.SAVE_SCHEDULE_OVERNIGHT_ERROR.DESCRIPTION', {
            days: [
              ...new Set(
                overnight.map(shift => dayjs(shift.date).format('D.MM'))
              )
            ].join(', ')
          }),
          'error'
        )
        return
      }

      inFlight.current.add(weekStart)
      let succeeded = false

      try {
        const saved = await ScheduleAPI.updateWeek(
          storeToWeekDto(state.shifts, state.committedColorsByUserId, weekStart)
        )

        const after = useScheduleStore.getState()
        // Shifts alone decide this: adopting the response's ids replaces this
        // week's shifts, and a colour picked mid-request has no bearing on
        // whether that is still safe. ANDing the two skipped adoption over a
        // colour change, and since the week's own content had not moved it also
        // read as clean afterwards, so nothing ever rewrote it and the shifts
        // kept their local ids for good, which blocks that week from ever
        // hydrating again.
        const shiftsUnchanged = serializeWeek(after.shifts, weekStart) === sent
        const colorsUnchanged =
          serializeColors(after.committedColorsByUserId) === sentColors

        if (shiftsUnchanged) {
          // Adopt the backend's ids so the next write updates these shifts
          // rather than recreating them. Skipped when the user kept editing
          // during the request, the response describes an older week, and
          // adopting it would throw away what they just did.
          //
          // The week is replaced by DATE RANGE, not by the dates present in
          // the response: emptying a week returns no shifts, and filtering on
          // those would leave every deleted shift sitting in the store.
          const mapped = weekDtoToStore(saved)
          useScheduleStore
            .getState()
            .replaceWeek(weekStart, mapped.shifts, mapped.colorsByUserId)

          const adopted = useScheduleStore.getState()
          useScheduleStore
            .getState()
            .markWeekSaved(weekStart, serializeWeek(adopted.shifts, weekStart))
        } else {
          // The server holds what was SENT, not what the store now shows. The
          // baseline has to say so: recording the newer state instead would
          // read as clean and quietly strand every edit made during the
          // request. Left like this the week stays dirty, and the follow-up
          // below writes it.
          useScheduleStore.getState().markWeekSaved(weekStart, sent)
        }

        // Settled on their own, because they are not this week's business. When
        // nothing moved locally the post-response store is the baseline, it
        // carries whatever the server merged back. When the user picked another
        // colour mid-request, the server holds what was SENT, so that is the
        // baseline and the difference keeps them dirty for one more write.
        useScheduleStore
          .getState()
          .markColorsSaved(
            colorsUnchanged
              ? serializeColors(
                  useScheduleStore.getState().committedColorsByUserId
                )
              : sentColors
          )

        succeeded = true

        // Only when it actually changes: `filter` returns a new array either
        // way, and with `unsavedWeeks` in the effect's dep array that forced a
        // render and an effect run after every single write.
        setUnsavedWeeks(weeks =>
          weeks.includes(weekStart)
            ? weeks.filter(week => week !== weekStart)
            : weeks
        )

        // The outcome the user sees. A toast rather than a line in the
        // toolbar: it is the app's own channel for "that worked", it says so
        // once and gets out of the way, and a permanent label reporting a
        // state nobody is worried about is furniture.
        //
        // "Updated", not "saved": the endpoint replaces a week that is already
        // there, so every write revises an existing roster rather than storing
        // one for the first time.
        showSnackbar(
          t('ALERTS.UPDATE_SCHEDULE_SUCCESS.TITLE'),
          t('ALERTS.UPDATE_SCHEDULE_SUCCESS.DESCRIPTION'),
          'success'
        )

        // The cached GET is dropped rather than refetched. Refetching cost a
        // full extra GET per save whose body could never be applied, a week
        // already in `hydratedWeeks` is never re-adopted, and a refetch does
        // not change `loadedKey`, so the hydration effect does not even re-run.
        // Worse, in the one case where a later hydration CAN adopt that week,
        // the refetch was racing it and routinely lost, so the PRE-write body
        // was adopted and the just-saved shift disappeared. Removing the entry
        // leaves nothing stale to adopt and costs no request.
        queryClient.removeQueries({
          queryKey: [
            SCHEDULE_QUERY.WEEK,
            localStorage.getItem('restaurantId'),
            weekStart
          ]
        })
      } catch (error) {
        setUnsavedWeeks(weeks =>
          weeks.includes(weekStart) ? weeks : [...weeks, weekStart]
        )

        // A 403 is not a transient failure: the week holds somebody who is no
        // longer a member of this restaurant, a shift another client has since
        // taken over, or an account without the shifts permission. "Try again"
        // would be wrong advice, the same payload fails the same way.
        const isForbidden =
          (error as { response?: { status?: number } })?.response?.status ===
          403

        showSnackbar(
          t(
            isForbidden
              ? 'ALERTS.SAVE_SCHEDULE_FORBIDDEN.TITLE'
              : 'ALERTS.SAVE_SCHEDULE_ERROR.TITLE'
          ),
          t(
            isForbidden
              ? 'ALERTS.SAVE_SCHEDULE_FORBIDDEN.DESCRIPTION'
              : 'ALERTS.SAVE_SCHEDULE_ERROR.DESCRIPTION'
          ),
          'error'
        )
      } finally {
        inFlight.current.delete(weekStart)

        // Edits that landed while the request was open were turned away by the
        // in-flight guard at the top, and nothing else will pick them up, the
        // effect only fires on a change, and theirs has already gone by.
        const latest = useScheduleStore.getState()
        const now = serializeWeek(latest.shifts, weekStart)

        // Only after a write that SUCCEEDED. On the failure path nothing
        // records a baseline, so both clauses are true whenever the user typed
        // during the request, and this re-fired the rejected payload at once -
        // no quiet period, no backoff, and for a 403 the same rejection every
        // time. A failure is left to `unsavedWeeks` and the retry button.
        if (
          succeeded &&
          now !== sent &&
          now !== latest.savedSnapshotByWeek[weekStart]
        ) {
          writeWeekRef.current(weekStart).catch(() => undefined)
        }
      }
    },
    [showSnackbar, queryClient, t]
  )

  // Held in a ref so the effect below can stay out of the callback's identity,
  // and so a write can chain a follow-up without referring to itself.
  const writeWeekRef = useRef(writeWeek)
  writeWeekRef.current = writeWeek

  useEffect(() => {
    if (!SCHEDULE_USE_API) return undefined

    // Only weeks read during THIS page load, the same rule the colour carrier
    // below already followed, and the one this hook's own docstring claims.
    // `savedSnapshotByWeek` is persisted and never pruned, so without the
    // filter a week left dirty by an earlier session (a write that failed, a
    // tab closed inside the quiet period, a copyWeek that never landed) was
    // pushed on mount before the user touched anything: a destructive
    // whole-week REPLACE of a week this client has not read, each with its own
    // "schedule updated" toast.
    const readThisSession = new Set(weeksReadThisSession())

    const pending = dirtyWeeks.filter(
      weekStart =>
        readThisSession.has(weekStart) &&
        attempted.current.get(weekStart) !== serializeWeek(shifts, weekStart)
    )

    // A colour change is not a change to any one week. Every week's PUT carries
    // the whole map, so if a week is going out anyway the new colours travel
    // with it for free; only when nothing else is pending does one week have to
    // be picked to carry them.
    //
    // Which one is picked matters a great deal, because a week PUT REPLACES
    // that week on the server:
    //  - only weeks read during THIS page load are eligible. `savedSnapshotByWeek`
    //    is persisted and never pruned, so it also names weeks last read days
    //    ago, pushing one of those would overwrite whatever has happened to it
    //    since, on this device or another;
    //  - weeks whose last write failed are skipped. Re-picking one means the
    //    same rejection every time, so the colours would never land, and the
    //    user gets an error naming a week they never touched;
    //  - weeks holding an overnight shift are skipped for the same reason -
    //    `writeWeek` refuses those before the request (see below), so a colour
    //    change would die there and report a day the user was not editing;
    //  - the most recent eligible week wins, as the likeliest to be on screen.
    const colorCarrier =
      colorsDirty && !pending.length
        ? weeksReadThisSession()
            .filter(
              weekStart =>
                savedSnapshotByWeek[weekStart] !== undefined &&
                !unsavedWeeks.includes(weekStart) &&
                !shiftsInWeek(shifts, weekStart).some(crossesMidnight)
            )
            .sort()
            .pop()
        : undefined

    const toWrite = colorCarrier ? [...pending, colorCarrier] : pending

    if (!toWrite.length) return undefined

    // Restarted by every keystroke-sized change, so a burst settles into one
    // write per week rather than one per edit.
    const timer = setTimeout(() => {
      toWrite.forEach(weekStart => {
        writeWeekRef.current(weekStart).catch(() => undefined)
      })
    }, QUIET_PERIOD_MS)

    return () => clearTimeout(timer)
    // `colorsSerialized`, not `colorsDirty`: the boolean stays true for a whole
    // colour drag, so the quiet period would never restart and the write would
    // go out mid-drag, several PUTs for one pick.
  }, [
    dirtyWeeks,
    shifts,
    colorsDirty,
    colorsSerialized,
    savedSnapshotByWeek,
    unsavedWeeks
  ])

  const retry = useCallback(() => {
    unsavedWeeks.forEach(weekStart => {
      // Cleared so the same content is allowed through again, this is the one
      // place a rejected payload is deliberately re-sent.
      attempted.current.delete(weekStart)
      writeWeekRef.current(weekStart).catch(() => undefined)
    })
  }, [unsavedWeeks])

  return {
    unsavedWeeks,
    retry,
    isEnabled: SCHEDULE_USE_API
  }
}
